import { useState, useRef, useCallback, useEffect } from 'react'
import type { AssetType } from '../types'
import { TagFilter } from './TagFilter'

interface CastVideo {
  key: string
  label: string
  url: string
}

interface Props {
  onCapture: (opts: {
    type: AssetType
    name: string
    tags: string[]
    imageBase64: string
    mimeType?: string
    sourceVideoId?: string
    timestampSec?: number
  }) => Promise<unknown>
}

export function FrameCapture({ onCapture }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [videos, setVideos] = useState<CastVideo[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedVideo, setSelectedVideo] = useState<CastVideo | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [capturedFrame, setCapturedFrame] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [assetType, setAssetType] = useState<AssetType>('character')
  const [assetName, setAssetName] = useState('')
  const [assetTags, setAssetTags] = useState<string[]>([])

  useEffect(() => {
    loadCastVideos()
  }, [])

  const loadCastVideos = async () => {
    setLoading(true)
    try {
      const res = await fetch(`/api/cast/status?_t=${Date.now()}`)
      const data = await res.json()
      const completed: CastVideo[] = []

      for (const [key, status] of Object.entries(data) as [string, any][]) {
        if (status.type === 'sequence' && status.state === 'done' && status.file) {
          completed.push({ key, label: key.split('/').pop() || key, url: status.file })
        } else if (status.state === 'done' && status.file) {
          completed.push({ key, label: key.split('/').pop() || key, url: status.file })
        }
      }

      setVideos(completed)
    } catch {
      setVideos([])
    } finally {
      setLoading(false)
    }
  }

  const handleVideoSelect = (video: CastVideo) => {
    setSelectedVideo(video)
    setCapturedFrame(null)
    setCurrentTime(0)
  }

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime)
    }
  }

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration)
    }
  }

  const handleScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = parseFloat(e.target.value)
    setCurrentTime(t)
    if (videoRef.current) {
      videoRef.current.currentTime = t
    }
  }

  const captureCurrentFrame = useCallback(() => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.drawImage(video, 0, 0)
    const dataUrl = canvas.toDataURL('image/png')
    setCapturedFrame(dataUrl)
  }, [])

  const handleStepFrame = (direction: -1 | 1) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime + direction * (1 / 30))
    }
  }

  const handleSave = async () => {
    if (!capturedFrame || !assetName.trim() || !selectedVideo) return
    setSaving(true)
    await onCapture({
      type: assetType,
      name: assetName.trim(),
      tags: assetTags,
      imageBase64: capturedFrame,
      mimeType: 'image/png',
      sourceVideoId: selectedVideo.key,
      timestampSec: currentTime,
    })
    setSaving(false)
    setCapturedFrame(null)
    setAssetName('')
    setAssetTags([])
  }

  const toggleTag = (tag: string) => {
    setAssetTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]))
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-forge-ember border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (videos.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-5">
        <div className="text-center">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-forge-cool-smoke mx-auto mb-3">
            <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <p className="text-[12px] text-forge-ash mb-1">No completed videos</p>
          <p className="text-[10px] text-forge-cool-smoke">Generate videos in Cast first, then come back to capture frames as assets</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Video list sidebar */}
      <div className="w-64 border-r border-forge-slate/50 overflow-auto">
        <div className="px-3 py-2 border-b border-forge-slate/50">
          <span className="text-[10px] uppercase tracking-wider text-forge-ash font-semibold">Completed Videos</span>
        </div>
        {videos.map((v) => (
          <button
            key={v.key}
            onClick={() => handleVideoSelect(v)}
            className={`w-full text-left px-3 py-2 text-[11px] border-b border-forge-slate/30 transition-colors ${
              selectedVideo?.key === v.key
                ? 'bg-forge-ember/10 text-forge-ember'
                : 'text-forge-ash hover:text-forge-primary hover:bg-forge-basalt/50'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Main capture area */}
      <div className="flex-1 flex flex-col overflow-auto p-5">
        {!selectedVideo ? (
          <div className="flex-1 flex items-center justify-center text-center">
            <div>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-forge-cool-smoke mx-auto mb-3">
                <polyline points="15 10 20 15 15 20" />
                <path d="M4 4v7a4 4 0 004 4h12" />
              </svg>
              <p className="text-[12px] text-forge-ash">Select a video to capture frames</p>
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto w-full space-y-4">
            {/* Video player */}
            <div className="rounded-lg overflow-hidden border border-forge-slate bg-black">
              <video
                ref={videoRef}
                src={selectedVideo.url}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                className="w-full max-h-[400px]"
                crossOrigin="anonymous"
              />
            </div>

            {/* Scrubber */}
            <div className="space-y-2">
              <input
                type="range"
                min={0}
                max={duration}
                step={0.033}
                value={currentTime}
                onChange={handleScrub}
                className="w-full accent-forge-ember"
              />
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-mono text-forge-ash tabular-nums">
                  {currentTime.toFixed(2)}s / {duration.toFixed(2)}s
                </span>
                <button
                  onClick={() => handleStepFrame(-1)}
                  className="text-[10px] px-2 py-1 rounded border border-forge-slate text-forge-ash hover:text-forge-primary transition-colors"
                >
                  ← Frame
                </button>
                <button
                  onClick={() => handleStepFrame(1)}
                  className="text-[10px] px-2 py-1 rounded border border-forge-slate text-forge-ash hover:text-forge-primary transition-colors"
                >
                  Frame →
                </button>
                <button
                  onClick={() => videoRef.current?.paused ? videoRef.current?.play() : videoRef.current?.pause()}
                  className="text-[10px] px-2 py-1 rounded border border-forge-slate text-forge-ash hover:text-forge-primary transition-colors"
                >
                  Play/Pause
                </button>
                <div className="flex-1" />
                <button
                  onClick={captureCurrentFrame}
                  className="text-[11px] font-semibold px-4 py-1.5 rounded-md bg-forge-ember text-white hover:bg-forge-ember/80 transition-colors"
                >
                  Capture Frame
                </button>
              </div>
            </div>

            {/* Captured frame + metadata form */}
            {capturedFrame && (
              <div className="border border-forge-ember/30 rounded-lg p-4 bg-forge-obsidian/50 space-y-3">
                <div className="flex gap-4">
                  <img src={capturedFrame} alt="Captured frame" className="w-48 rounded-md border border-forge-slate" />
                  <div className="flex-1 space-y-3">
                    <div className="flex gap-3">
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-forge-ash font-semibold block mb-1">Type</label>
                        <div className="flex rounded-md border border-forge-slate overflow-hidden">
                          {(['character', 'scene'] as const).map((t) => (
                            <button
                              key={t}
                              onClick={() => setAssetType(t)}
                              className={`text-[11px] font-semibold px-3 py-1.5 transition-colors ${
                                assetType === t
                                  ? 'bg-forge-ember text-white'
                                  : 'text-forge-ash hover:text-forge-primary'
                              }`}
                            >
                              {t === 'character' ? 'Character' : 'Scene'}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="flex-1">
                        <label className="text-[10px] uppercase tracking-wider text-forge-ash font-semibold block mb-1">Name</label>
                        <input
                          type="text"
                          value={assetName}
                          onChange={(e) => setAssetName(e.target.value)}
                          placeholder="Name this asset..."
                          className="w-full bg-forge-obsidian border border-forge-slate rounded-md px-3 py-1.5 text-[12px] text-forge-primary placeholder:text-forge-cool-smoke focus:border-forge-ember focus:outline-none"
                        />
                      </div>
                    </div>

                    <TagFilter type={assetType} selectedTags={assetTags} onToggleTag={toggleTag} compact />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-forge-slate/30">
                  <button
                    onClick={() => setCapturedFrame(null)}
                    className="text-[11px] font-semibold rounded-md px-3 py-1.5 border border-forge-slate text-forge-ash hover:text-forge-primary transition-colors"
                  >
                    Discard
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={!assetName.trim() || saving}
                    className={`text-[11px] font-semibold rounded-md px-4 py-1.5 transition-colors ${
                      assetName.trim() && !saving
                        ? 'bg-forge-ember text-white hover:bg-forge-ember/80'
                        : 'bg-forge-slate text-forge-cool-smoke cursor-not-allowed'
                    }`}
                  >
                    {saving ? 'Saving...' : 'Save to Foundry'}
                  </button>
                </div>
              </div>
            )}

            <canvas ref={canvasRef} className="hidden" />
          </div>
        )}
      </div>
    </div>
  )
}
